import type { BriefStatus, ProjectBrief, Task, TaskAttemptUsage, TaskStatus } from '@dyspatch/core';

/**
 * Which brief states still accept a comment.
 *
 * Mirrored from core's `COMMENTABLE_BRIEF_STATUSES` rather than imported,
 * because importing a *value* from `@dyspatch/core` pulls the barrel, and with
 * it `node:crypto`, into a browser bundle. Same trade as `event-kinds.ts`, and
 * safe for the same reason: a test pins this list to core's, so adding a
 * commentable status without adding it here fails `bun test` rather than
 * silently hiding the comment box.
 *
 * The rule itself: after Go the conversation moves to the PR.
 */
export const COMMENTABLE_STATUSES: readonly BriefStatus[] = [
  'draft',
  'decomposing',
  'awaiting_approval',
];

export function isCommentable(brief: ProjectBrief): boolean {
  return COMMENTABLE_STATUSES.includes(brief.status);
}

/**
 * Which brief states each of the two stop verbs is legal from, mirrored from
 * core's `REJECTABLE_BRIEF_STATUSES` and `CANCELLABLE_BRIEF_STATUSES` for the
 * same reason the commentable list is, and pinned by the same test.
 *
 * The split is the point: rejection is the pre-Go refusal and touches only work
 * no machine holds, while cancellation is what stops an approved brief and the
 * runs already out on people's laptops. Offering either one where the hub
 * refuses it is a button whose only output is a toast.
 *
 * `REJECTABLE_STATUSES` has no predicate of its own yet: the console offers
 * Reject only at the approval gate, which is a subset of it. It is mirrored here
 * because the guard it pins is the other half of the same rule.
 */
export const REJECTABLE_STATUSES: readonly BriefStatus[] = [
  'draft',
  'decomposing',
  'awaiting_approval',
];

export const CANCELLABLE_STATUSES: readonly BriefStatus[] = ['approved'];

export function isCancellable(brief: ProjectBrief): boolean {
  return CANCELLABLE_STATUSES.includes(brief.status);
}

/**
 * Which brief states can be amended (docs/JOB-LIFECYCLE.md 2.6): after Go, and
 * after the job finished, which the amendment reopens. Mirrored from the route's
 * own guard in `admin.ts`; before Go the plan is commented on or rejected.
 */
export const AMENDABLE_STATUSES: readonly BriefStatus[] = ['approved', 'done'];

export function isAmendable(brief: ProjectBrief): boolean {
  return AMENDABLE_STATUSES.includes(brief.status);
}

/** Pause reaches dispatched work, so it is offered where cancel is: after Go. */
export function isPausable(brief: ProjectBrief): boolean {
  return CANCELLABLE_STATUSES.includes(brief.status);
}

/** The steps an amendment added and nobody has approved yet: the gate is open again. */
export function addedDrafts(brief: ProjectBrief, tasks: readonly Task[]): Task[] {
  if (brief.status !== 'approved') return [];
  return tasks.filter((task) => task.status === 'draft');
}

/** Steps carrying the pause flag, whatever their status. */
export function pausedTasks(tasks: readonly Task[]): Task[] {
  return tasks.filter((task) => Boolean(task.pausedAt));
}

/**
 * The statuses a pause means something in, mirrored from db's
 * `PAUSABLE_TASK_STATUSES` for the same reason the brief lists above are
 * mirrored, and pinned by the same kind of test.
 *
 * A job is `approved` from Go until its last step ends, so the brief's status
 * alone cannot say whether there is anything left to hold: a job whose every
 * step has failed is still `approved`. Without this the console drew "Pause
 * job" over such a job, and the press could only come back as a refusal.
 */
export const PAUSABLE_TASK_STATUSES: readonly TaskStatus[] = [
  'queued',
  'offered',
  'claimed',
  'running',
  'reporting',
  'review',
];

/** Steps a press of Pause would actually hold: unfinished, and not already held. */
export function pausableTasks(tasks: readonly Task[]): Task[] {
  return tasks.filter((task) => PAUSABLE_TASK_STATUSES.includes(task.status) && !task.pausedAt);
}

/** Whether this brief's plan graph is still a plan rather than history. */
export function showsPlanGraph(brief: ProjectBrief): boolean {
  return isCommentable(brief);
}

/**
 * Whether this brief is waiting for somebody to ask for a plan.
 *
 * A draft with no tasks is a brief nothing will ever happen to on its own: the
 * hub never calls a model, so the graph only arrives after a human asks for one.
 *
 * The other two halves of the condition are both about the one brief that is
 * `draft` *with* tasks on it: one whose planning ran out of attempts, which the
 * hub put back to draft with the error on record and refuses to plan over
 * ("reject it and submit a new brief"). That one is revived by requeueing its
 * failed PLAN task, and offering this verb for it would be a button whose only
 * possible output is a 409 toast. `decomposeError` is checked as well as the
 * count because the count comes from a task list the hub caps at 500 rows: a
 * brief the list does not reach must not look unplanned.
 */
export function awaitsPlan(brief: ProjectBrief, taskCount: number): boolean {
  return brief.status === 'draft' && !brief.decomposeError && taskCount === 0;
}

/**
 * Whether this job is waiting on a human decision right now.
 *
 * The job page opens with the gate and pins it under the header, because a
 * decision is why most people opened the job, and it needs one question
 * answered to know whether to draw that region at all. There are exactly three
 * open gates a job can have and they are mutually exclusive by construction:
 * "nobody has asked for a plan" is only true of a draft, "approve the plan" only
 * of `awaiting_approval`, and "approve the steps an amendment added" only of an
 * approved job (`addedDrafts` checks the status itself).
 *
 * Its own function rather than three conditions on the page because the page
 * asks it twice - once to decide whether to pin the region, once to tint the
 * state row underneath - and because it is the one piece of that page worth a
 * test.
 */
export function hasOpenGate(brief: ProjectBrief, tasks: readonly Task[]): boolean {
  return (
    awaitsPlan(brief, tasks.length) ||
    brief.status === 'awaiting_approval' ||
    addedDrafts(brief, tasks).length > 0
  );
}

/** The same question across a list: which briefs have nobody drafting anything. */
export function briefsAwaitingPlan(
  briefs: readonly ProjectBrief[],
  tasks: readonly Task[],
): ProjectBrief[] {
  const planned = new Set(tasks.map((task) => task.briefId));
  return briefs
    .filter((brief) => awaitsPlan(brief, planned.has(brief.id) ? 1 : 0))
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

/**
 * The three groups the briefs list is read in, in the order a person acts on
 * them: what is waiting on a human, what is moving, what is over. The same
 * grouping the server-rendered page used, closed briefs capped the same way.
 */
export interface BriefGroup {
  key: string;
  label: string;
  briefs: ProjectBrief[];
  empty: string;
}

/**
 * The statuses that mean a step is being worked on right now, mirrored from db's
 * task lifecycle for the reason every list in this file is mirrored, and pinned
 * by a test.
 *
 * `review` counts. A step in review is waiting on a person rather than on a
 * machine, but the job it belongs to is moving: there is a branch, there is a
 * pull request, and the next thing that happens to it is a decision somebody
 * makes. A job whose only live step is in review is emphatically not stalled.
 */
export const ACTIVE_TASK_STATUSES: readonly TaskStatus[] = [
  'claimed',
  'running',
  'reporting',
  'review',
];

export function hasActiveStep(tasks: readonly Task[]): boolean {
  return tasks.some((task) => ACTIVE_TASK_STATUSES.includes(task.status));
}

/**
 * Why an approved job with no live step is sitting there, in one clause.
 *
 * The hub already wrote the answer: the sweeper stamps `holdReason` and
 * `holdExplanation` on every step it refuses to offer, and the explanation is a
 * whole sentence about the fleet ("all 1 registered workers are offline"). What
 * the list needs is shorter than that and about the job rather than the step, so
 * this reduces the steps to the one fact that decides the row.
 *
 * The order is the order a person can act in. A paused job is the reader's own
 * doing and comes first. A hold only a person can clear is the one they can do
 * something about. Everything else is the scheduler waiting on the world, and
 * saying which flavour of waiting would be a longer sentence carrying no extra
 * decision.
 */
export function waitingReason(tasks: readonly Task[]): string | null {
  const live = tasks.filter(
    (task) => task.status !== 'done' && task.status !== 'cancelled' && task.status !== 'failed',
  );
  if (!live.length) return null;
  if (live.some((task) => task.pausedAt)) return 'paused';
  const held = live.filter((task) => task.status === 'queued' && task.holdReason);
  if (held.some((task) => task.holdReason === 'all_previously_tried')) {
    return 'every device has tried it';
  }
  if (held.some((task) => task.holdReason === 'all_at_capacity')) return 'the fleet is full';
  if (held.length) return 'waiting for a device';
  if (live.some((task) => task.status === 'draft')) return 'tasks waiting on approval';
  return 'queued';
}

/**
 * The groups the Jobs page is read in, over the briefs **and their steps**.
 *
 * `approved` used to be enough to call a job in progress, and it is not: it
 * means a person pressed Go. Thirteen jobs approved in one sitting against one
 * worker at a concurrency of one all appeared under "In progress" with a spinner
 * each, one of them running and twelve held on `all_at_capacity` with nothing on
 * the row saying so, which made the one job that was actually running impossible
 * to find. So the grouping asks the steps: a job is in progress when at least
 * one step is claimed, running, reporting or in review, and waiting otherwise.
 *
 * `tasks` is every task the page already loaded, not this brief's: the page
 * loads them once for the palette and the steps count, and a per-brief map built
 * here is one pass rather than one query per row. A brief with no tasks in the
 * map is a brief whose steps the list did not reach (the task list is capped),
 * and it is called waiting rather than running, because claiming a job is moving
 * on evidence you do not have is the failure this whole change is about.
 *
 * `exclude` is the set of ids some region above these groups has already drawn.
 * The Jobs page opens with "Stalled before planning", which is every draft with
 * no plan and a Request plan button on each row. Those drafts are also working
 * by this function's own definition, so the page listed all of them twice, forty
 * pixels apart, once with the verb and once without. Two rows for one job is a
 * page a person has to reconcile before they can read it, and the second copy
 * carried strictly less than the first.
 */
export function briefGroups(
  briefs: readonly ProjectBrief[],
  tasks: readonly Task[] = [],
  exclude?: ReadonlySet<string>,
): BriefGroup[] {
  const byBrief = new Map<string, Task[]>();
  for (const task of tasks) {
    const bucket = byBrief.get(task.briefId);
    if (bucket) bucket.push(task);
    else byBrief.set(task.briefId, [task]);
  }

  const gates = briefs
    .filter((brief) => brief.status === 'awaiting_approval')
    .sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt));

  const open = briefs.filter(
    (brief) =>
      !exclude?.has(brief.id) &&
      (brief.status === 'draft' || brief.status === 'decomposing' || brief.status === 'approved'),
  );
  // Decomposing is a machine doing something right now, whatever its steps say:
  // the plan is being drafted and there are no steps yet to ask.
  const running = open.filter(
    (brief) => brief.status === 'decomposing' || hasActiveStep(byBrief.get(brief.id) ?? []),
  );
  const waiting = open.filter((brief) => !running.includes(brief));

  const closed = briefs
    .filter(
      (brief) =>
        brief.status === 'done' || brief.status === 'rejected' || brief.status === 'failed',
    )
    .slice(0, 20);

  return [
    {
      key: 'gate',
      label: 'Awaiting approval',
      briefs: gates,
      empty: 'No plans are waiting on approval.',
    },
    {
      key: 'working',
      label: 'In progress',
      briefs: running,
      empty: 'Nothing is being planned or executed.',
    },
    {
      key: 'waiting',
      label: 'Waiting',
      briefs: waiting,
      empty: 'Nothing is dispatched and waiting.',
    },
    { key: 'closed', label: 'Closed', briefs: closed, empty: 'Nothing has closed yet.' },
  ];
}

/** Open briefs, the count the nav chip carries. */
export function openBriefs(briefs: readonly ProjectBrief[]): ProjectBrief[] {
  return briefs.filter(
    (brief) => brief.status !== 'done' && brief.status !== 'rejected' && brief.status !== 'failed',
  );
}

/**
 * The reserved key of the synthetic planning task, mirrored from core's
 * `PLAN_TASK_KEY` for the same reason the status lists are, and pinned by the
 * same test. It is a task on the brief, but it is not part of the work: its
 * product was the graph.
 */
export const PLAN_KEY = 'PLAN';

/**
 * The reserved keys of the steps the hub filed for itself: the planning task
 * and the integration checks (`CHECK`, `CHECK-2`, `CHECK-3`, issue #26).
 * Mirrored from core's `isSyntheticTaskKey` for the same reason `PLAN_KEY` is,
 * and pinned to it by the same test. Neither is work the job asked for, so
 * progress, cost and the graph all leave them out.
 */
export function isSyntheticKey(key: string): boolean {
  return key === PLAN_KEY || /^CHECK(-[1-9][0-9]*)?$/.test(key);
}

export interface BriefProgress {
  done: number;
  total: number;
  /**
   * Counted steps carrying a branch or a PR, whether or not they have merged.
   * A held step keeps both by design (14 days, docs/JOB-LIFECYCLE.md decision
   * 2), so without this a job where every step is stuck reads exactly like one
   * where nothing has run at all: "0 of 7 done" either way (issue #29).
   */
  produced: number;
}

/**
 * The steps a brief's own progress is measured over.
 *
 * Two exclusions, both so the fraction can be trusted. The planning task is not
 * work the brief is asking for, and counting it would have a one-task brief read
 * "1 of 2" the moment it was approved. Cancelled tasks leave the denominator
 * too: they are work that is no longer going to happen, and a brief whose
 * remaining five tasks are all merged should read "5 of 5" rather than sitting
 * at "5 of 7 done" for ever.
 *
 * Named rather than inlined in `briefProgress` because the dots beside the
 * fraction (docs/JOB-LIFECYCLE.md 2.8) draw one per step, and "3 of 5 done"
 * beside six dots is a row a person has to reconcile before they can read it.
 */
export function countedTasks(tasks: readonly Task[]): Task[] {
  return tasks.filter((task) => !isSyntheticKey(task.key) && task.status !== 'cancelled');
}

/** Null unless at least one side has a number - an unreported attempt adds nothing (issue #18, mirrors `@dyspatch/db`'s `sumNullable`). */
function addUsage(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

/** `TaskAttemptUsage` with every counter at zero and no attempts summed into it yet. */
const NO_USAGE: TaskAttemptUsage = {
  model: null,
  inputTokens: null,
  outputTokens: null,
  cacheCreationTokens: null,
  cacheReadTokens: null,
  costNanoUsd: null,
  costEstimated: null,
  agentDurationMs: null,
  runningDurationMs: null,
};

/** Once any contributing step's cost was an estimate, the job total it feeds is an estimate too - mirrors `@dyspatch/db`'s `orNullable`. */
function orUsage(a: boolean | null | undefined, b: boolean | null | undefined): boolean | null {
  if (a == null && b == null) return null;
  return Boolean(a) || Boolean(b);
}

function sumUsage(a: TaskAttemptUsage, b: TaskAttemptUsage | null | undefined): TaskAttemptUsage {
  return {
    model: null,
    inputTokens: addUsage(a.inputTokens, b?.inputTokens),
    outputTokens: addUsage(a.outputTokens, b?.outputTokens),
    cacheCreationTokens: addUsage(a.cacheCreationTokens, b?.cacheCreationTokens),
    cacheReadTokens: addUsage(a.cacheReadTokens, b?.cacheReadTokens),
    costNanoUsd: addUsage(a.costNanoUsd, b?.costNanoUsd),
    costEstimated: orUsage(a.costEstimated, b?.costEstimated),
    agentDurationMs: addUsage(a.agentDurationMs, b?.agentDurationMs),
    runningDurationMs: addUsage(a.runningDurationMs, b?.runningDurationMs),
  };
}

export interface JobUsageTotals {
  /** Sum of `totalUsage` over every step except the plan. Null with nothing to sum yet. */
  work: TaskAttemptUsage | null;
  /** The planning task's own total, shown apart from the work it produced (issue #18). */
  plan: TaskAttemptUsage | null;
}

/**
 * A job's cost, with the planning run counted separately from the six steps
 * it produced - a plan that cost 100k tokens to draft is a fact worth seeing
 * next to the work, not folded into the same number (issue #18's own framing).
 */
export function jobUsageTotals(tasks: readonly Task[]): JobUsageTotals {
  const plan = tasks.find((task) => task.key === PLAN_KEY)?.totalUsage ?? null;
  const work = tasks
    .filter((task) => !isSyntheticKey(task.key))
    .reduce<TaskAttemptUsage | null>((sum, task) => {
      if (!task.totalUsage) return sum;
      return sumUsage(sum ?? NO_USAGE, task.totalUsage);
    }, null);
  return { work, plan };
}

/**
 * When a job's fraction is worth drawing at all.
 *
 * Before Go it would read "0 of 7 done" on a plan nobody has approved, which
 * says nothing about work that has not been agreed to yet. After Go it is the
 * state of the work. A job that has finished keeps it: "8 of 8 done" is the
 * answer to the question the row is asking, and a fraction that disappears at
 * the moment it would read best is a row that got worse as it got further
 * along.
 *
 * `failed` is deliberately not here. A failed job's fraction is true but it is
 * not the thing to say about it, and the state word already says the thing.
 *
 * Named because three surfaces draw the fraction (the job pane, the jobs list
 * and the graph, docs/JOB-LIFECYCLE.md 2.8) and they were three copies of one
 * condition, one of which had drifted.
 */
export function showsProgress(brief: { status: BriefStatus }): boolean {
  return brief.status === 'approved' || brief.status === 'done';
}

/** How much of a brief has landed, as "3 of 7 done", over exactly those steps. */
export function briefProgress(tasks: readonly Task[]): BriefProgress {
  const counted = countedTasks(tasks);
  return {
    done: counted.filter((task) => task.status === 'done').length,
    total: counted.length,
    produced: counted.filter((task) => task.status === 'done' || task.branch || task.prUrl).length,
  };
}

/**
 * "2 pushed", to sit beside "0 of 7 done" wherever a job is stuck rather than
 * untouched: `null` once every produced step is already counted as done, so
 * the clause only ever adds a fact the fraction alone was hiding (issue #29).
 */
export function producedNote(progress: BriefProgress): string | null {
  const pending = progress.produced - progress.done;
  return pending > 0 ? `${pending} pushed` : null;
}
